import _ from 'lodash';
import React, { useMemo } from 'react';
import MuiTextField, { OutlinedTextFieldProps } from '@material-ui/core/TextField';

import { makeStyles } from '../styles';

const useFormHelperTextStyles = makeStyles<{ size?: string }>({
  root: {
    // helperText is off by 1px in large, but not medium (otherwise large would be 16px)
    margin: props => `8px ${props.size === 'large' ? 13 : 12}px 0`,
    fontSize: '11px',
    lineHeight: '16px',
    letterSpacing: '0.1px',
  },
});

const useLabelStyles = makeStyles(theme => ({
  root: {
    height: '100%',
    transform: 'translate(14px, 16px) scale(1)',
  },
  disabled: {
    color: `${theme.colors.gray20} !important`,
  },
}));

const useInputStyles = makeStyles<{
  size?: string;
  padding?: string;
  marginLeft?: string;
  minHeight?: string;
  borderRadius?: string;
  borderWidth?: string;
}>(theme => ({
  root: {
    '&:hover,&$focused,&:focus-within $notchedOutline': {
      borderColor: theme.colors.periwinkle100,
    },
  },
  notchedOutline: {
    borderRadius: props => props.borderRadius || theme.spacing(1),
    borderColor: theme.colors.gray20,
    borderWidth: props => `${props.borderWidth || '1px'} !important`,
  },
  multiline: {
    padding: 0,
  },
  input: {
    fontSize: 14,
    padding: props =>
      props.padding ? props.padding : props.size === 'large' ? '14px 16px' : '6px 12px',
    lineHeight: '20px',
    height: 'auto',
    '&::placeholder': {
      color: theme.colors.gray50,
      opacity: 1,
    },
    minHeight: props => props.minHeight,
    marginLeft: props => props.marginLeft,
  },
  disabled: {
    '&::placeholder': {
      color: theme.colors.gray20,
    },
    input: {
      fontSize: 14,
      padding: props =>
        props.padding ? props.padding : props.size === 'large' ? '14px 16px' : '6px 12px',
      lineHeight: '20px',
      height: 'auto',
      '&::placeholder': {
        color: theme.colors.gray50,
        opacity: 1,
      },
      marginLeft: props => props.marginLeft,
    },
    disabled: {
      '&::placeholder': {
        color: theme.colors.gray20,
      },
    },
  },
}));

type AllowedMuiTextFieldProps = Pick<
  OutlinedTextFieldProps,
  | 'className'
  | 'disabled'
  | 'error'
  | 'fullWidth'
  | 'helperText'
  | 'inputRef'
  | 'inputProps'
  | 'InputProps'
  | 'InputLabelProps'
  | 'multiline'
  | 'onKeyDown'
  | 'onChange'
  | 'onPaste'
  | 'onBlur'
  | 'placeholder'
  | 'value'
  | 'rows'
  | 'type'
  | 'autoFocus'
  | 'onFocus'
  | 'id'
  | 'rowsMax'
  | 'defaultValue'
  | 'required'
  | 'style'
  | 'name'
  | 'autoComplete'
  | 'maxRows'
  | 'onMouseLeave'
>;

type TextInputClassKey = 'input' | 'root';

interface RegularTextInputProps extends AllowedMuiTextFieldProps {
  size?: 'medium';
  /** Regular inputs should NOT use a nested label */
  label?: never;
  /** Regular inputs can use a placeholder instead of label */
  placeholder?: string;
  classes?: Partial<Record<TextInputClassKey, string | undefined>>;
  startAdornment?: any;
  endAdornment?: any;
  padding?: string;
  marginLeft?: string; // Used to accommodate icons
  borderRadius?: string;
}

interface LargeTextInputProps extends AllowedMuiTextFieldProps {
  /** Large inputs MUST use a nested label */
  label: string;
  /** Large inputs may use a placeholder in addition to label */
  placeholder?: string;
  size: 'large';
  classes?: Partial<Record<TextInputClassKey, string>>;
  startAdornment?: any;
  endAdornment?: any;
  padding?: string;
  minHeight?: string;
  borderRadius?: string;
  borderWidth?: string;
}

export type TextInputProps = RegularTextInputProps | LargeTextInputProps;

const TextInput: React.FC<TextInputProps> = React.forwardRef<HTMLDivElement, TextInputProps>(
  function TextInput(props, ref) {
    const styleProps = _.pick(props, [
      'padding',
      'size',
      'marginLeft',
      'minHeight',
      'borderRadius',
      'borderWidth',
    ]);
    const formHelperTextClasses = useFormHelperTextStyles(styleProps);
    const inputClasses = useInputStyles(styleProps);
    const labelClasses = useLabelStyles();

    const classes = useMemo(
      () =>
        props.classes?.root
          ? {
              root: props.classes.root,
            }
          : undefined,
      [props.classes?.root],
    );

    return (
      <MuiTextField
        ref={ref}
        classes={classes}
        variant="outlined"
        FormHelperTextProps={{ classes: formHelperTextClasses }}
        InputLabelProps={{ classes: labelClasses }}
        InputProps={{
          classes: inputClasses,
          startAdornment: props.startAdornment,
          endAdornment: props.endAdornment,
        }}
        onWheel={(e: any) => e.target.blur()}
        {..._.omit(
          props,
          'size',
          'classes',
          'startAdornment',
          'endAdornment',
          'minHeight',
          'borderRadius',
          'borderWidth',
        )}
      />
    );
  },
);

export default TextInput;
